import { useEffect, useState, useCallback } from 'react';
import { apiFetch as get, apiPost as post } from '../api/client';
import { PageContainer } from '../components/PageContainer';

interface Candidate {
  id: string;
  title: string;
  previewUrl: string;
  aestheticScore: number | null;
  aestheticNotes: string | null;
  colorPalette: string | null;
  subjectCategory: string | null;
  qualifyingSizes: unknown;
  status: string;
  discoveredAt: string;
  // detail-only fields
  description?: string | null;
  attributionText?: string | null;
  fullResUrl?: string | null;
  pixelWidth?: number | null;
  pixelHeight?: number | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type SortOption = 'score_desc' | 'score_asc' | 'created_desc' | 'resolution_desc';
type ScoredFilter = 'all' | 'true' | 'false';
type OrientationChoice = 'portrait' | 'landscape';

const ACCENT = '#a0a8ff';
const BG = '#111118';
const CARD_BG = '#1a1a24';
const BORDER = '#2a2a3a';
const DANGER = '#ff6b6b';
const SUCCESS = '#6bffb8';

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) {
    return (
      <span style={{
        fontSize: 11, padding: '2px 7px', borderRadius: 999,
        background: '#2a2a3a', color: '#888', fontWeight: 600,
      }}>
        unscored
      </span>
    );
  }
  const color = score >= 75 ? SUCCESS : score >= 50 ? ACCENT : '#ffb347';
  return (
    <span style={{
      fontSize: 11, padding: '2px 7px', borderRadius: 999,
      background: color + '22', color, fontWeight: 700, letterSpacing: 0.5,
    }}>
      {score}
    </span>
  );
}

interface QualifyingSize {
  sizeLabel: string;
  orientation: string;
  productType: string;
}

function QualifyingSizes({ sizes }: { sizes: unknown }) {
  const arr = Array.isArray(sizes) ? (sizes as QualifyingSize[]) : [];
  if (arr.length === 0) return <span style={{ color: '#666' }}>None</span>;

  const seen = new Set<string>();
  const grouped: Record<string, string[]> = {};
  for (const s of arr) {
    const key = `${s.productType}:${s.sizeLabel}`;
    if (seen.has(key)) continue;
    seen.add(key);
    (grouped[s.productType] ??= []).push(s.sizeLabel);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {Object.entries(grouped).map(([type, labels]) => (
        <div key={type}>
          <span style={{ color: '#666', textTransform: 'capitalize' }}>{type}: </span>
          {labels.join(', ')}
        </div>
      ))}
    </div>
  );
}

/** Suggest an orientation based on the image's own aspect ratio */
function suggestOrientation(w: number | null | undefined, h: number | null | undefined): OrientationChoice {
  return (w ?? 0) >= (h ?? 1) ? 'landscape' : 'portrait';
}

function DetailModal({
                       candidate,
                       onClose,
                       onApprove,
                       onReject,
                     }: {
  candidate: Candidate;
  onClose: () => void;
  onApprove: (id: string, orientation: OrientationChoice) => Promise<void>;
  onReject: (id: string) => Promise<void>;
}) {
  const [acting, setActing] = useState<'approve' | 'reject' | null>(null);
  const [confirmReject, setConfirmReject] = useState(false);

  // Orientation selection — shown after first Approve click
  const [pickingOrientation, setPickingOrientation] = useState(false);
  const [orientation, setOrientation] = useState<OrientationChoice>(
    suggestOrientation(candidate.pixelWidth, candidate.pixelHeight)
  );

  const handleApproveClick = () => {
    if (!pickingOrientation) {
      // First click: reveal the orientation picker
      setPickingOrientation(true);
      return;
    }
    // Second click (Confirm): proceed with the chosen orientation
    handleConfirmApprove();
  };

  const handleConfirmApprove = async () => {
    setActing('approve');
    await onApprove(candidate.id, orientation);
    setActing(null);
  };

  const handleReject = async () => {
    if (!confirmReject) { setConfirmReject(true); return; }
    setActing('reject');
    await onReject(candidate.id);
    setActing(null);
  };

  const orientationOptions: { value: OrientationChoice; label: string; icon: string }[] = [
    { value: 'portrait',  label: 'Portrait',  icon: '▯' },
    { value: 'landscape', label: 'Landscape', icon: '▭' },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: CARD_BG, border: `1px solid ${BORDER}`,
          borderRadius: 12, width: '100%', maxWidth: 780,
          maxHeight: '90vh', overflowY: 'auto', padding: 32,
          display: 'flex', flexDirection: 'column', gap: 24,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#fff', lineHeight: 1.3, maxWidth: '85%' }}>
            {candidate.title}
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#888',
            fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 0,
          }}>✕</button>
        </div>

        {/* Thumbnail */}
        <div style={{
          width: '100%', maxHeight: 380, borderRadius: 8,
          overflow: 'hidden', background: '#0a0a12',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img
            src={candidate.previewUrl}
            alt={candidate.title}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>

        {/* Full-res link */}
        {candidate.fullResUrl && (
          <a
            href={candidate.fullResUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              alignSelf: 'flex-start', fontSize: 12, color: ACCENT,
              textDecoration: 'none', border: `1px solid ${ACCENT}44`,
              borderRadius: 6, padding: '5px 12px',
            }}
          >
            Open full-res image ↗
          </a>
        )}

        {/* Metadata grid */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
          fontSize: 13, color: '#ccc',
        }}>
          <div>
            <div style={{ color: '#888', marginBottom: 4 }}>Aesthetic Score</div>
            <ScoreBadge score={candidate.aestheticScore} />
          </div>
          <div>
            <div style={{ color: '#888', marginBottom: 4 }}>Category</div>
            <span>{candidate.subjectCategory ?? '—'}</span>
          </div>
          <div>
            <div style={{ color: '#888', marginBottom: 4 }}>Color Palette</div>
            <span style={{ fontStyle: 'italic', color: '#bbb' }}>
              {candidate.colorPalette ?? '—'}
            </span>
          </div>
          <div>
            <div style={{ color: '#888', marginBottom: 4 }}>Dimensions</div>
            <span>
              {candidate.pixelWidth && candidate.pixelHeight
                ? `${candidate.pixelWidth.toLocaleString()} × ${candidate.pixelHeight.toLocaleString()} px`
                : '—'}
            </span>
          </div>
          <div>
            <div style={{ color: '#888', marginBottom: 4 }}>Qualifying Sizes</div>
            <QualifyingSizes sizes={candidate.qualifyingSizes} />
          </div>
        </div>

        {/* Score notes */}
        {candidate.aestheticNotes && (
          <div style={{ fontSize: 13, color: '#bbb', lineHeight: 1.6 }}>
            <div style={{ color: '#888', marginBottom: 4 }}>Scoring Notes</div>
            {candidate.aestheticNotes}
          </div>
        )}

        {/* Description */}
        {candidate.description && (
          <div style={{ fontSize: 13, color: '#bbb', lineHeight: 1.6 }}>
            <div style={{ color: '#888', marginBottom: 4 }}>Description</div>
            {candidate.description}
          </div>
        )}

        {/* Attribution — prominent, non-editable */}
        <div style={{
          background: '#0f0f1a', border: `1px solid ${BORDER}`,
          borderRadius: 8, padding: '12px 16px',
        }}>
          <div style={{ color: ACCENT, fontSize: 11, fontWeight: 700, marginBottom: 6, letterSpacing: 0.8 }}>
            ATTRIBUTION (required — cannot be removed)
          </div>
          <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.6, userSelect: 'all' }}>
            {candidate.attributionText ?? 'No attribution text available'}
          </div>
        </div>

        {/* Orientation picker — revealed after first Approve click */}
        {pickingOrientation && (
          <div style={{
            background: '#0f0f1a', border: `1px solid ${ACCENT}44`,
            borderRadius: 8, padding: '16px 20px',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <div style={{ fontSize: 12, color: ACCENT, fontWeight: 700, letterSpacing: 0.8 }}>
              SELECT PRODUCT ORIENTATION
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              Determines which print sizes are offered and how the image is cropped for the print file.
              Square sizes are always included.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {orientationOptions.map(opt => {
                const isSelected = orientation === opt.value;
                const isSuggested = opt.value === suggestOrientation(candidate.pixelWidth, candidate.pixelHeight);
                return (
                  <button
                    key={opt.value}
                    onClick={() => setOrientation(opt.value)}
                    style={{
                      flex: 1, padding: '10px 8px', borderRadius: 8,
                      border: `1px solid ${isSelected ? ACCENT : BORDER}`,
                      background: isSelected ? ACCENT + '22' : 'transparent',
                      color: isSelected ? ACCENT : '#888',
                      cursor: 'pointer', fontSize: 13, fontWeight: isSelected ? 700 : 400,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 20, lineHeight: 1 }}>{opt.icon}</span>
                    <span>{opt.label}</span>
                    {isSuggested && (
                      <span style={{ fontSize: 10, color: '#555' }}>suggested</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setConfirmReject(false);
              setPickingOrientation(false);
              onClose();
            }}
            style={{
              padding: '10px 20px', borderRadius: 8, border: `1px solid ${BORDER}`,
              background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 13,
            }}
          >
            Close
          </button>
          {pickingOrientation && (
            <button
              onClick={() => setPickingOrientation(false)}
              style={{
                padding: '10px 20px', borderRadius: 8, border: `1px solid ${BORDER}`,
                background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 13,
              }}
            >
              ← Back
            </button>
          )}
          <button
            onClick={handleReject}
            disabled={acting !== null}
            style={{
              padding: '10px 20px', borderRadius: 8,
              border: `1px solid ${DANGER}44`,
              background: confirmReject ? DANGER + '22' : 'transparent',
              color: DANGER, cursor: 'pointer', fontSize: 13,
              opacity: acting !== null ? 0.5 : 1,
            }}
          >
            {acting === 'reject' ? 'Rejecting…' : confirmReject ? 'Confirm Reject' : 'Reject'}
          </button>
          <button
            onClick={handleApproveClick}
            disabled={acting !== null}
            style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: acting !== null ? '#2a2a3a' : ACCENT,
              color: acting !== null ? '#555' : '#111',
              fontWeight: 700, cursor: acting !== null ? 'default' : 'pointer', fontSize: 13,
              opacity: acting !== null ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
          >
            {acting === 'approve'
              ? 'Approving…'
              : pickingOrientation
                ? `Approve as ${orientation.charAt(0).toUpperCase() + orientation.slice(1)}`
                : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CandidateCard({
                         candidate,
                         onClick,
                       }: {
  candidate: Candidate;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: CARD_BG, border: `1px solid ${BORDER}`,
        borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = ACCENT + '88')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = BORDER)}
    >
      <div style={{ position: 'relative', paddingBottom: '62%', background: '#0a0a12' }}>
        <img
          src={candidate.previewUrl}
          alt={candidate.title}
          loading="lazy"
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover',
          }}
        />
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <ScoreBadge score={candidate.aestheticScore} />
        </div>
      </div>
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{
          fontSize: 13, color: '#ddd', fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {candidate.title}
        </div>
        <span style={{ fontSize: 11, color: '#666' }}>
          {candidate.subjectCategory ?? 'uncategorized'}
        </span>
        {candidate.colorPalette && (
          <div style={{
            fontSize: 11, color: '#888', fontStyle: 'italic',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {candidate.colorPalette}
          </div>
        )}
      </div>
    </div>
  );
}

export default function QueuePage() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sort, setSort] = useState<SortOption>('score_desc');
  const [scored, setScored] = useState<ScoredFilter>('all');
  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [pageJump, setPageJump] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailCandidate, setDetailCandidate] = useState<Candidate | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        sort,
        scored,
      });
      if (minScore) params.set('minScore', minScore);
      if (maxScore) params.set('maxScore', maxScore);

      const data = await get<{ candidates: Candidate[]; pagination: Pagination }>(
        `/queue?${params}`
      );
      setCandidates(data.candidates);
      setPagination(data.pagination);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sort, scored, minScore, maxScore]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const data = await get<Candidate>(`/queue/${id}`);
      setDetailCandidate(data);
    } catch {
      setDetailCandidate(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetailCandidate(null);
  };

  const handleApprove = async (id: string, orientation: OrientationChoice) => {
    await post(`/queue/${id}/approve`, { orientation });
    closeDetail();
    fetchQueue();
  };

  const handleReject = async (id: string) => {
    await post(`/queue/${id}/reject`, { reason: 'rejected_manual' });
    closeDetail();
    fetchQueue();
  };

  const inputStyle = {
    background: '#0f0f1a', border: `1px solid ${BORDER}`,
    borderRadius: 6, color: '#ccc', padding: '6px 10px',
    fontSize: 13, outline: 'none',
  };

  const selectStyle = { ...inputStyle, cursor: 'pointer' };

  return (
    <PageContainer>
      <div style={{ padding: 32, maxWidth: 1200, margin: '0 auto', color: '#ccc' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 24, color: '#fff' }}>Review Queue</h1>
        <p style={{ margin: '0 0 28px', color: '#666', fontSize: 14 }}>
          {pagination ? `${pagination.total} pending candidates` : 'Loading…'}
        </p>

        {/* Filters */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 28,
          padding: '16px 20px', background: CARD_BG,
          border: `1px solid ${BORDER}`, borderRadius: 10,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#666' }}>Sort</label>
            <select
              value={sort}
              onChange={e => { setSort(e.target.value as SortOption); setPage(1); }}
              style={selectStyle}
            >
              <option value="score_desc">Score: High → Low</option>
              <option value="score_asc">Score: Low → High</option>
              <option value="created_desc">Newest First</option>
              <option value="resolution_desc">Resolution: High → Low</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#666' }}>Scored</label>
            <select
              value={scored}
              onChange={e => { setScored(e.target.value as ScoredFilter); setPage(1); }}
              style={selectStyle}
            >
              <option value="all">All</option>
              <option value="true">Scored only</option>
              <option value="false">Unscored only</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#666' }}>Min Score</label>
            <input
              type="number" min={0} max={100} value={minScore}
              onChange={e => { setMinScore(e.target.value); setPage(1); }}
              placeholder="0" style={{ ...inputStyle, width: 70 }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#666' }}>Max Score</label>
            <input
              type="number" min={0} max={100} value={maxScore}
              onChange={e => { setMaxScore(e.target.value); setPage(1); }}
              placeholder="100" style={{ ...inputStyle, width: 70 }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: '#666' }}>Page Size</label>
            <select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={selectStyle}
            >
              <option value={24}>24</option>
              <option value={48}>48</option>
              <option value={96}>96</option>
              <option value={200}>200</option>
            </select>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => { setSort('score_desc'); setScored('all'); setMinScore(''); setMaxScore(''); setPageSize(24); setPage(1); }}
              style={{
                padding: '6px 14px', borderRadius: 6, border: `1px solid ${BORDER}`,
                background: 'transparent', color: '#666', cursor: 'pointer', fontSize: 12,
              }}
            >
              Reset
            </button>
          </div>
        </div>

        {/* Grid */}
        {error && (
          <div style={{ color: DANGER, marginBottom: 20, fontSize: 14 }}>{error}</div>
        )}

        {loading ? (
          <div style={{ color: '#666', fontSize: 14 }}>Loading…</div>
        ) : candidates.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px 0',
            color: '#555', fontSize: 15,
          }}>
            No candidates match your filters.
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 16,
          }}>
            {candidates.map(c => (
              <CandidateCard key={c.id} candidate={c} onClick={() => openDetail(c.id)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 32,
          }}>
            <div style={{ fontSize: 12, color: '#555' }}>
              Showing {(page - 1) * pagination.limit + 1}–
              {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                style={{
                  padding: '8px 14px', borderRadius: 6, border: `1px solid ${BORDER}`,
                  background: 'transparent', color: page === 1 ? '#444' : '#ccc',
                  cursor: page === 1 ? 'default' : 'pointer', fontSize: 13,
                }}
              >
                « First
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: `1px solid ${BORDER}`,
                  background: 'transparent', color: page === 1 ? '#444' : '#ccc',
                  cursor: page === 1 ? 'default' : 'pointer', fontSize: 13,
                }}
              >
                ← Prev
              </button>
              <span style={{ padding: '8px 12px', fontSize: 13, color: '#666' }}>
              {page} / {pagination.totalPages}
            </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: `1px solid ${BORDER}`,
                  background: 'transparent',
                  color: page === pagination.totalPages ? '#444' : '#ccc',
                  cursor: page === pagination.totalPages ? 'default' : 'pointer', fontSize: 13,
                }}
              >
                Next →
              </button>
              <button
                onClick={() => setPage(pagination.totalPages)}
                disabled={page === pagination.totalPages}
                style={{
                  padding: '8px 14px', borderRadius: 6, border: `1px solid ${BORDER}`,
                  background: 'transparent',
                  color: page === pagination.totalPages ? '#444' : '#ccc',
                  cursor: page === pagination.totalPages ? 'default' : 'pointer', fontSize: 13,
                }}
              >
                Last »
              </button>

              <form
                onSubmit={e => {
                  e.preventDefault();
                  const target = parseInt(pageJump);
                  if (!isNaN(target)) {
                    setPage(Math.min(pagination.totalPages, Math.max(1, target)));
                  }
                  setPageJump('');
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 12 }}
              >
                <input
                  type="number"
                  min={1}
                  max={pagination.totalPages}
                  value={pageJump}
                  onChange={e => setPageJump(e.target.value)}
                  placeholder="Go to…"
                  style={{
                    background: '#0f0f1a', border: `1px solid ${BORDER}`,
                    borderRadius: 6, color: '#ccc', padding: '6px 8px',
                    fontSize: 13, outline: 'none', width: 64,
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: `1px solid ${BORDER}`,
                    background: 'transparent', color: '#ccc', cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Go
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {selectedId !== null && (
          detailLoading ? (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, color: '#666', fontSize: 14,
            }}>
              Loading…
            </div>
          ) : detailCandidate ? (
            <DetailModal
              candidate={detailCandidate}
              onClose={closeDetail}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ) : null
        )}
      </div>
    </PageContainer>
  );
}